/**
 * hrv.js — pure-function HRV calculations from RR-interval streams.
 *
 * RR-intervals are the millisecond gaps between consecutive R-peaks of the
 * QRS complex. Bluetooth LE Heart Rate Service characteristic 0x2A37 can
 * deliver them directly (in 1/1024-second resolution, convertible to ms).
 *
 * All functions are pure — they take arrays in and return numbers out. No
 * side-effects, no component state. Unit-testable with plain JS. That
 * decoupling matters because HRV math is the one place where a subtle bug
 * (wrong formula, wrong window, stale input) would produce plausible-but-
 * wrong user-facing numbers — and biofeedback users trust numbers. Keeping
 * the math in one file with a narrow API makes it easy to reason about
 * and audit later.
 *
 * Glossary:
 *   - RR (NN) interval: ms between consecutive heartbeats
 *   - RMSSD:  Root Mean Square of Successive Differences — parasympathetic
 *             (vagal) tone indicator. Gold-standard live HRV metric.
 *   - SDNN:   Standard deviation of all NN intervals — longer-term HRV.
 *   - pNN50:  Percentage of pairs of adjacent NN intervals differing by
 *             more than 50 ms. Simple, robust.
 *   - Rhythm Score: our own 0–100 metric. See computeRhythmScore() for
 *             definition. NOT the same as HeartMath's trademarked
 *             "Coherence Score" (that uses spectral analysis).
 */

// Convert a BLE 1/1024-sec RR tick to milliseconds. The spec is fixed at
// 1024 Hz regardless of the strap model.
export const bleRrToMs = (ticks) => (ticks * 1000) / 1024;

/**
 * RMSSD — sqrt( mean( (RRn - RRn-1)^2 ) )
 * Needs at least 2 intervals. Returns null if input too short.
 */
/**
 * Filter implausible RR-intervals before any HRV calculation.
 *
 * Chest straps regularly emit garbage values when contact briefly drops
 * (sweat-loss, motion, electrode lift): the next "RR" they report is
 * actually two-or-three combined beats summed together, e.g. 2000ms or
 * 3000ms instead of the real ~1000ms. RMSSD squares the diff between
 * consecutive RRs, so a single contaminated transition can inflate the
 * score by 5-10×.
 *
 * Standard practice in HRV literature (Kubios, Polar's own algorithms,
 * PhysioNet HRV toolkit) is to drop intervals outside an absolute
 * physiologic envelope (300-2000ms = 30-200 BPM, the boundaries of
 * possible adult heart rate) AND outside a local-median tolerance band
 * (we use ±25%, the conservative Kubios default for "low" artifact
 * correction). We discard rather than interpolate — interpolation can
 * mask issues that should be visible in HRV metrics; discarding gives
 * an honest "skipped beat" gap instead.
 */
export const sanitizeRr = (rrMs) => {
  if (!Array.isArray(rrMs) || rrMs.length === 0) return [];
  // 1) Absolute physiologic envelope
  const ABS_MIN = 300;   // 200 BPM ceiling — anything faster is artifact
  const ABS_MAX = 2000;  // 30 BPM floor — anything slower means missed beats
  const inEnvelope = rrMs.filter((v) => v >= ABS_MIN && v <= ABS_MAX);
  if (inEnvelope.length < 3) return inEnvelope;
  // 2) Local-median rejection. Median is robust to the outliers we're
  // trying to remove, unlike the mean which they'd skew.
  const sorted = [...inEnvelope].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  const tolerance = 0.25; // ±25% — Kubios "low correction" preset
  return inEnvelope.filter(
    (v) => Math.abs(v - median) / median <= tolerance
  );
};

/**
 * RMSSD — root mean square of successive differences. The gold-standard
 * time-domain HRV metric. Computed over sanitized RR intervals so
 * artifact-contaminated readings don't blow the score up to physiologic
 * impossibilities (300+ ms when a clean read tops out around 100 ms).
 */
export const computeRmssd = (rrMs) => {
  const clean = sanitizeRr(rrMs);
  if (clean.length < 2) return null;
  let sumSq = 0;
  let count = 0;
  for (let i = 1; i < clean.length; i++) {
    const diff = clean[i] - clean[i - 1];
    sumSq += diff * diff;
    count += 1;
  }
  return Math.sqrt(sumSq / count);
};

/**
 * SDNN — standard deviation of all NN intervals. Ddf=0 (population stdev)
 * is the convention in HRV literature.
 */
export const computeSdnn = (rrMs) => {
  const clean = sanitizeRr(rrMs);
  if (clean.length < 2) return null;
  const mean = clean.reduce((a, b) => a + b, 0) / clean.length;
  const varSum = clean.reduce((acc, v) => acc + (v - mean) ** 2, 0);
  return Math.sqrt(varSum / clean.length);
};

/**
 * pNN50 — percentage of |RRn - RRn-1| > 50 ms. Bounded 0..100.
 */
export const computePnn50 = (rrMs) => {
  const clean = sanitizeRr(rrMs);
  if (clean.length < 2) return null;
  let over = 0;
  let total = 0;
  for (let i = 1; i < clean.length; i++) {
    if (Math.abs(clean[i] - clean[i - 1]) > 50) over += 1;
    total += 1;
  }
  return total > 0 ? (over / total) * 100 : 0;
};

/**
 * Rhythm Score — our 0–100 readout approximating HRV "coherence" without
 * infringing on HeartMath's trademark or spectral-analysis patents. We
 * reward *stability* of RMSSD over a 60-second window: if the variability
 * of your variability is low, your autonomic nervous system is operating
 * smoothly (which is what coherence is about, intuitively).
 *
 * Formula: take two halves of the supplied RR window, compute RMSSD of
 * each, then score = 100 × ( 1 / ( 1 + |rmssd_a - rmssd_b| / rmssd_mean ) ).
 *
 * Intuition: identical halves → score = 100. Wildly different halves →
 * score approaches 0. Clamps gracefully at extremes. Needs at least 6
 * intervals to produce a meaningful value.
 */
export const computeRhythmScore = (rrMs) => {
  if (!Array.isArray(rrMs) || rrMs.length < 6) return null;
  const mid = Math.floor(rrMs.length / 2);
  const a = rrMs.slice(0, mid);
  const b = rrMs.slice(mid);
  const ra = computeRmssd(a);
  const rb = computeRmssd(b);
  if (ra == null || rb == null) return null;
  const mean = (ra + rb) / 2;
  if (mean <= 0) return null;
  const delta = Math.abs(ra - rb);
  const raw = 100 * (1 / (1 + delta / mean));
  return Math.max(0, Math.min(100, raw));
};

/**
 * BPM from a running mean of RR — more stable than the strap's
 * instantaneous HR reading because it averages the same window we use
 * for HRV. 60000 ms / mean_rr_ms = BPM. Sanitized so dropped-beat
 * artifacts don't pull the displayed BPM into impossibly-low territory.
 */
export const computeBpmFromRr = (rrMs) => {
  const clean = sanitizeRr(rrMs);
  if (clean.length === 0) return null;
  const mean = clean.reduce((a, b) => a + b, 0) / clean.length;
  return mean > 0 ? 60000 / mean : null;
};

/**
 * Sliding-window buffer — drops oldest intervals once total duration
 * exceeds `windowMs`. Mutates the array in-place for speed (called many
 * times per second).
 */
export const trimToWindow = (rrMs, windowMs) => {
  let total = rrMs.reduce((a, b) => a + b, 0);
  while (total > windowMs && rrMs.length > 0) {
    total -= rrMs.shift();
  }
  return rrMs;
};

/**
 * Parse a Heart Rate Measurement characteristic (0x2A37) DataView into
 * { bpm, rrIntervals } per the BLE HR spec. Returns rrIntervals in
 * milliseconds, already converted from the 1/1024-sec source units.
 *
 * Flags byte layout (bit 0 LSB):
 *   bit 0:  HR value format — 0 = UINT8, 1 = UINT16
 *   bit 1-2: sensor contact status bits (ignored here)
 *   bit 3:  Energy Expended present (skip 2 bytes if set)
 *   bit 4:  RR-Interval values present
 */
export const parseHeartRateMeasurement = (dv) => {
  if (!dv || typeof dv.getUint8 !== 'function') return { bpm: null, rrIntervals: [] };
  const flags = dv.getUint8(0);
  const hrIs16 = (flags & 0x01) === 0x01;
  const hasEnergy = (flags & 0x08) === 0x08;
  const hasRr = (flags & 0x10) === 0x10;

  let offset = 1;
  const bpm = hrIs16 ? dv.getUint16(offset, true) : dv.getUint8(offset);
  offset += hrIs16 ? 2 : 1;

  if (hasEnergy) offset += 2;

  const rrIntervals = [];
  if (hasRr) {
    while (offset + 1 < dv.byteLength) {
      const ticks = dv.getUint16(offset, true);
      rrIntervals.push(bleRrToMs(ticks));
      offset += 2;
    }
  }

  return { bpm, rrIntervals };
};
